import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';

export default function MobileDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentProFormas, setRecentProFormas] = useState([]);

  useEffect(() => {
    async function load() {
      const [invoiceRes, proFormaRes] = await Promise.all([
        supabase.from('invoices')
          .select('id, invoice_number, status, total_amount, currency_id, created_at, clients(full_name)')
          .neq('status', 'VOID')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase.from('quotations')
          .select('id, quotation_number, status, total_amount, currency:currency_id(code), created_at, clients(full_name)')
          .neq('status', 'VOID')
          .order('created_at', { ascending: false })
          .limit(5),
      ]);

      const invoices = invoiceRes.data || [];
      const unpaid = invoices.filter(i => i.status !== 'PAID');
      setStats({
        totalInvoices: invoices.length,
        unpaidCount: unpaid.length,
      });
      setRecentInvoices(invoices);
      setRecentProFormas(proFormaRes.data || []);
    }
    load();
  }, []);

  const username = user?.email?.split('@')[0] || '';

  return (
    <div style={{ padding: '20px 16px', paddingBottom: 80 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a2e' }}>Good day, {username} 👋</div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* Stat cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: '16px 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', borderLeft: '4px solid #f07040' }}>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Recent Invoices</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e' }}>{stats.totalInvoices}</div>
          </div>
          <div style={{ background: 'white', borderRadius: 12, padding: '16px 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Unpaid</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: stats.unpaidCount > 0 ? '#dc2626' : '#16a34a' }}>{stats.unpaidCount}</div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Quick Access</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { label: 'Invoices', icon: '🧾', path: '/m/invoices' },
            { label: 'Pro Formas', icon: '📋', path: '/m/proformas' },
            { label: 'Clients', icon: '👥', path: '/m/clients' },
            { label: 'Quotations', icon: '📝', path: '/m/quotations' },
          ].map(item => (
            <button key={item.path} onClick={() => navigate(item.path)}
              style={{ background: 'white', border: '1px solid #eee', borderRadius: 12, padding: '18px 14px', textAlign: 'left', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{item.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent invoices */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Recent Invoices</div>
        <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          {recentInvoices.length === 0 && <div style={{ padding: '20px 16px', color: '#aaa', fontSize: 13 }}>No invoices yet</div>}
          {recentInvoices.map((inv, i) => (
            <div key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: i < recentInvoices.length - 1 ? '1px solid #f3f3f3' : 'none', cursor: 'pointer' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#f07040' }}>{inv.invoice_number}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{inv.clients?.full_name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {inv.status === 'PAID' && <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 2 }}>PAID</div>}
                <div style={{ fontSize: 12, color: '#888' }}>{inv.created_at?.slice(0, 10)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Pro Formas */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Recent Pro Formas</div>
        <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          {recentProFormas.length === 0 && <div style={{ padding: '20px 16px', color: '#aaa', fontSize: 13 }}>No pro formas yet</div>}
          {recentProFormas.map((q, i) => (
            <div key={q.id} onClick={() => navigate(`/quotations/${q.id}`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: i < recentProFormas.length - 1 ? '1px solid #f3f3f3' : 'none', cursor: 'pointer' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#f07040' }}>{q.quotation_number}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{q.clients?.full_name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {q.status === 'CONVERTED' && <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 2 }}>CONVERTED</div>}
                <div style={{ fontSize: 12, color: '#888' }}>{q.currency?.code} {parseFloat(q.total_amount || 0).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
